import { useState } from "react";
import { dateOf, focusOptions, imprintRows, money, yearOf } from "@bd/sim";
import { Icon, Sheet } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { t, tdate, tx } from "../i18n";
import { BackTitle } from "./Meta";
import { IconVal } from "./Products";

/** Publishers you bought out keep publishing under their own name, for you: what they earn and what they focus on. */
export function Imprints() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const co = me(game)!;
  const [error, setError] = useState<string | null>(null);
  const rows = imprintRows(game, co);
  const opts = focusOptions(game);
  const total = rows.reduce((s, r) => s + r.yearly + r.catalogueYearly, 0);
  const pct = (m: number) => `${m >= 1 ? "+" : "−"}${Math.round(Math.abs(m - 1) * 100)}%`;
  const focus = async (rival: string, value: string) => {
    const r = await cmd({ type: "imprintFocus", rival, focus: value });
    setError(r.ok ? null : tx(r.error));
  };
  return (
    <Sheet title={<BackTitle title={t("Imprints")} onBack={() => set({ screen: { kind: "products" } })} />} badge={rows.length > 0 ? <span className="ichip" title={t("{m} a year", { m: money(total) })}><Icon name="coin" size={15} />{t("{m}/yr", { m: money(total) })}</span> : undefined} onClose={() => set({ screen: null })} wide drawer testId="imprints">
      {rows.length === 0 && <p className="muted" style={{ marginTop: 0 }}>{t("Buy out a rival on the Money screen.")}</p>}
      {error && <p style={{ color: "var(--bad)", marginTop: 0 }} data-testid="imprint-error">{error}</p>}
      <div className="stack" style={{ gap: 6 }}>
        {rows.map((r) => {
          const can = r.refocusAt <= game.week;
          return (
            <div key={r.rival} className="card con-card" data-testid="imprint">
              <span className="con-main">
                <strong>{r.name}</strong>
                <span className="ivals">
                  <IconVal icon="calendar" label={t("Yours since {year}", { year: yearOf(r.since) })}>{yearOf(r.since)}</IconVal>
                  <IconVal icon="coin" label={t("New games {m}/yr", { m: money(r.yearly) })}>{t("{m}/yr", { m: money(r.yearly) })}</IconVal>
                  {r.catalogueYearly > 0 && r.catalogueUntil !== null && <IconVal icon="layers" label={t("Catalogue {m}/yr until {year}", { m: money(r.catalogueYearly), year: yearOf(r.catalogueUntil) })}>{t("{m}/yr", { m: money(r.catalogueYearly) })} → {yearOf(r.catalogueUntil)}</IconVal>}
                  {r.focus ? <IconVal icon="trend" label={t("Focus {pct}", { pct: pct(r.mult) })}>{pct(r.mult)}</IconVal> : null}
                </span>
              </span>
              <span className="con-book" title={t("An audience focus is steady; a theme focus rides its craze. Once a year.")}>
                {!can && <span className="ival muted" title={t("Can change {date}", { date: tdate(dateOf(r.refocusAt).label) })}><Icon name="lock" size={13} />{tdate(dateOf(r.refocusAt).label)}</span>}
                <select value={r.focus ?? ""} disabled={!can} onChange={(e) => void focus(r.rival, e.target.value)} aria-label={t("Focus")} data-testid="imprint-focus">
                  <option value="">{t("Their own mix")}</option>
                  <optgroup label={t("Audiences")}>
                    {opts.filter((o) => o.kind === "audience").map((o) => <option key={o.id} value={o.id}>{t(o.name)}</option>)}
                  </optgroup>
                  <optgroup label={t("Themes")}>
                    {opts.filter((o) => o.kind === "theme").map((o) => <option key={o.id} value={o.id}>{t(o.name)}</option>)}
                  </optgroup>
                </select>
              </span>
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
